import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import quoteService from "../../../../services/quoteService";
import { getNodeServiceBaseUrl, getAssetServiceBaseUrl } from "../../../../config/apiBase";
import { ModulePage } from "../../../../shared-ui/ModulePage";
import "../../../../shared-ui/crane-tokens.css";
import "../../../../shared-ui/stitch-forms.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";
const authHeaders = () => ({
  Authorization: `Bearer ${token()}`,
  "Content-Type": "application/json",
  "X-Client": "web",
});

const todayIso = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

function taxFromQuoteLine(line) {
  const igst = Number(line.igst_pct || 0);
  const cgst = Number(line.cgst_pct || 0);
  const sgst = Number(line.sgst_pct || 0);
  if (igst > 0) return { tax_type: "IGST", tax_percentage: igst };
  if (cgst + sgst > 0) return { tax_type: "CGST-SGST", tax_percentage: cgst + sgst };
  return { tax_type: "", tax_percentage: 0 };
}

/**
 * Collects full WO master + line-item fields before converting an APPROVED quote.
 * Silent one-click convert is removed — assets/dates/HSD are required like manual WO create.
 */
export default function QuotationConvertWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [assets, setAssets] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);

  const [master, setMaster] = useState({
    wo_no: "",
    wo_date: todayIso(),
    wo_start: todayIso(),
    wo_end: plusDays(30),
    sector: "",
    no_shifts: 1,
    lunch: false,
    include_woff: false,
    remarks: "",
  });

  const [lines, setLines] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr("");
      try {
        const [qRes, aRes, sRes] = await Promise.all([
          quoteService.get(id),
          fetch(`${getAssetServiceBaseUrl()}/api/assets/`, { headers: authHeaders() }),
          fetch(`${getNodeServiceBaseUrl()}/api/get-column/common_master/sector`, { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        const q = qRes.data;
        if (!q) throw new Error("Quote not found");
        if (q.status !== "APPROVED") {
          throw new Error(`Quote must be APPROVED to convert (current: ${q.status})`);
        }
        setQuote(q);

        const aBody = await aRes.json().catch(() => ({}));
        const assetList = Array.isArray(aBody)
          ? aBody
          : Array.isArray(aBody.data)
            ? aBody.data
            : Array.isArray(aBody.message)
              ? aBody.message
              : [];
        setAssets(assetList.filter((a) => a && a.id != null && a.active !== false));

        const sBody = await sRes.json().catch(() => ({}));
        const sectorList = Array.isArray(sBody)
          ? sBody
          : Array.isArray(sBody.data)
            ? sBody.data
            : [];
        setSectors(sectorList.filter((s) => s && s.id != null));

        const start = todayIso();
        const end = q.valid_until ? String(q.valid_until).slice(0, 10) : plusDays(30);
        setMaster((m) => ({
          ...m,
          wo_no: `WO-${String(q.quote_no || id).replace(/\W+/g, "").slice(-10)}`,
          wo_start: start,
          wo_end: end >= start ? end : plusDays(30),
          sector: sectorList[0]?.id ? String(sectorList[0].id) : "",
          remarks: `Converted from quote ${q.quote_no}`,
        }));

        setLines(
          (q.lines || []).map((l) => {
            const tax = taxFromQuoteLine(l);
            return {
              quote_line_id: l.id,
              description: l.description || "",
              asset_id: "",
              start_date: start,
              end_date: end >= start ? end : plusDays(30),
              quantity: Number(l.qty) || 1,
              unit: l.uom || "DAY",
              rate: Number(l.rate) || 0,
              hsd_scope: "",
              main_equipment: true,
              working_hrs: 8,
              monthly_working_days: 26,
              tax_type: tax.tax_type,
              tax_percentage: tax.tax_percentage,
            };
          })
        );
      } catch (e) {
        if (!cancelled) setErr(e.message || String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const assetLabel = (a) =>
    a.asset_code || a.code || a.name || a.asset_name || `Asset #${a.id}`;

  const setLine = (idx, patch) => {
    setLines((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const validateMaster = () => {
    if (!master.wo_no.trim()) return "Work order number is required";
    if (!master.wo_date) return "WO date is required";
    if (!master.wo_start || !master.wo_end) return "WO start and end dates are required";
    if (master.wo_end < master.wo_start) return "WO end must be on/after start";
    if (!master.sector) return "Sector is required";
    return "";
  };

  const validateLines = () => {
    if (!lines.length) return "At least one line is required";
    for (let i = 0; i < lines.length; i++) {
      const L = lines[i];
      if (!L.asset_id) return `Line ${i + 1}: select an asset`;
      if (!L.unit) return `Line ${i + 1}: unit is required`;
      if (!(Number(L.quantity) > 0)) return `Line ${i + 1}: quantity must be > 0`;
      if (Number.isNaN(Number(L.rate)) || Number(L.rate) < 0) return `Line ${i + 1}: rate is required`;
      if (!L.hsd_scope) return `Line ${i + 1}: HSD scope is required`;
      if (!L.start_date || !L.end_date) return `Line ${i + 1}: dates are required`;
      if (L.end_date < L.start_date) return `Line ${i + 1}: end before start`;
      if (L.start_date < master.wo_start || L.end_date > master.wo_end) {
        return `Line ${i + 1}: dates must be within WO period`;
      }
    }
    return "";
  };

  const goStep2 = () => {
    const v = validateMaster();
    if (v) {
      setErr(v);
      return;
    }
    setErr("");
    setLines((rows) =>
      rows.map((r) => ({
        ...r,
        start_date: r.start_date || master.wo_start,
        end_date: r.end_date || master.wo_end,
        working_hrs: Number(master.no_shifts) === 1 ? 8 : Number(master.no_shifts) === 2 ? 12 : 24,
      }))
    );
    setStep(2);
  };

  const submit = async () => {
    const vm = validateMaster();
    if (vm) {
      setErr(vm);
      setStep(1);
      return;
    }
    const vl = validateLines();
    if (vl) {
      setErr(vl);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        master: {
          wo_no: master.wo_no.trim(),
          wo_date: master.wo_date,
          wo_start: master.wo_start,
          wo_end: master.wo_end,
          sector: Number(master.sector),
          no_shifts: Number(master.no_shifts) || 1,
          lunch: Boolean(master.lunch),
          include_woff: Boolean(master.include_woff),
          remarks: master.remarks,
        },
        lines: lines.map((L) => ({
          quote_line_id: L.quote_line_id,
          asset_id: Number(L.asset_id),
          start_date: L.start_date,
          end_date: L.end_date,
          quantity: Number(L.quantity),
          unit: L.unit,
          rate: Number(L.rate),
          hsd_scope: L.hsd_scope,
          main_equipment: Boolean(L.main_equipment),
          working_hrs: Number(L.working_hrs) || 8,
          monthly_working_days: Number(L.monthly_working_days) || 26,
          tax_type: L.tax_type || undefined,
          tax_percentage: Number(L.tax_percentage) || 0,
        })),
      };
      const r = await quoteService.convertWo(id, payload);
      const woId = r.data?.work_order?.id;
      if (woId) navigate(`/work-orders/${woId}`);
      else navigate("/work-orders");
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const customerLabel = useMemo(() => {
    if (!quote) return "—";
    return quote.customer_name || `Customer #${quote.customer_id}`;
  }, [quote]);

  if (err && !quote) {
    return (
      <ModulePage crumbs={["Operations", "ZOrder", "Quotations"]} title="Convert quote → WO">
        <div className="crane-card" style={{ padding: 16 }}>
          <p style={{ color: "#ba1a1a" }}>{err}</p>
          <Link to="/quotations">Back to quotations</Link>
        </div>
      </ModulePage>
    );
  }

  if (!quote) {
    return (
      <ModulePage crumbs={["Operations", "ZOrder", "Quotations"]} title="Convert quote → WO">
        <p>Loading quote…</p>
      </ModulePage>
    );
  }

  return (
    <ModulePage
      crumbs={["Operations", "ZOrder", "Quotations", "Convert"]}
      title={`Convert ${quote.quote_no} → Work Order`}
      description="Fill every field required for a normal work order (sector, dates, assets, HSD). Quotation lines are prefilled but do not create WO lines until you complete this form."
      actions={
        <Link className="crane-btn" to={`/quotations/${id}`}>
          Cancel
        </Link>
      }
    >
      <div className="stitch-form-shell crane-card" style={{ padding: 16 }}>
        {err && (
          <div role="alert" style={{ color: "#ba1a1a", marginBottom: 12 }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 16, fontSize: 13 }}>
          <span className={step === 1 ? "crane-badge crane-badge--ok" : "crane-badge"}>1. WO header</span>
          <span className={step === 2 ? "crane-badge crane-badge--ok" : "crane-badge"}>2. Line items</span>
        </div>

        <div className="crane-card" style={{ padding: 12, marginBottom: 16, background: "var(--crane-surface-low,#eff4ff)" }}>
          <strong>Quote</strong> {quote.quote_no} · {customerLabel} · Project #{quote.project_id} · Taxable{" "}
          {quote.taxable_amt}
        </div>

        {step === 1 && (
          <div className="form-main-panel" style={{ padding: 0 }}>
            <h2>Work order header</h2>
            <div className="form-row">
              <div className="form-group">
                <label>WO number *</label>
                <input value={master.wo_no} onChange={(e) => setMaster({ ...master, wo_no: e.target.value })} />
              </div>
              <div className="form-group">
                <label>WO date *</label>
                <input type="date" value={master.wo_date} onChange={(e) => setMaster({ ...master, wo_date: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start *</label>
                <input type="date" value={master.wo_start} onChange={(e) => setMaster({ ...master, wo_start: e.target.value })} />
              </div>
              <div className="form-group">
                <label>End *</label>
                <input type="date" value={master.wo_end} onChange={(e) => setMaster({ ...master, wo_end: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Sector *</label>
                <select value={master.sector} onChange={(e) => setMaster({ ...master, sector: e.target.value })}>
                  <option value="">Select sector</option>
                  {sectors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.value || s.sector || s.name || s.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Shifts *</label>
                <select
                  value={master.no_shifts}
                  onChange={(e) => setMaster({ ...master, no_shifts: Number(e.target.value) })}
                >
                  <option value={1}>1 shift (8h)</option>
                  <option value={2}>2 shifts (12h)</option>
                  <option value={3}>3 shifts (24h)</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={master.lunch}
                    onChange={(e) => setMaster({ ...master, lunch: e.target.checked })}
                  />{" "}
                  Lunch
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={master.include_woff}
                    onChange={(e) => setMaster({ ...master, include_woff: e.target.checked })}
                  />{" "}
                  Include weekly off
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Remarks</label>
              <textarea rows={2} value={master.remarks} onChange={(e) => setMaster({ ...master, remarks: e.target.value })} />
            </div>
            <div style={{ padding: 16, display: "flex", gap: 8 }}>
              <button type="button" className="crane-btn crane-btn--primary" onClick={goStep2}>
                Next: line items →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.04em" }}>Line items</h2>
            <p style={{ fontSize: 13, color: "#3f4850" }}>
              Each quotation line needs an <strong>asset</strong> and <strong>HSD scope</strong> (same as manual WO create).
            </p>
            {lines.map((L, idx) => (
              <div key={L.quote_line_id || idx} className="crane-card" style={{ padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Line {idx + 1}: {L.description || "(no description)"}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Asset *</label>
                    <select value={L.asset_id} onChange={(e) => setLine(idx, { asset_id: e.target.value })}>
                      <option value="">Select asset</option>
                      {assets.map((a) => (
                        <option key={a.id} value={a.id}>
                          {assetLabel(a)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>HSD scope *</label>
                    <select value={L.hsd_scope} onChange={(e) => setLine(idx, { hsd_scope: e.target.value })}>
                      <option value="">Select</option>
                      <option value="CLIENT">CLIENT</option>
                      <option value="OWNER">OWNER</option>
                      <option value="NA">NA</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start *</label>
                    <input type="date" value={L.start_date} onChange={(e) => setLine(idx, { start_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>End *</label>
                    <input type="date" value={L.end_date} onChange={(e) => setLine(idx, { end_date: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Qty *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={L.quantity}
                      onChange={(e) => setLine(idx, { quantity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit *</label>
                    <input value={L.unit} onChange={(e) => setLine(idx, { unit: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Rate *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={L.rate}
                      onChange={(e) => setLine(idx, { rate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Working hrs</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={L.working_hrs}
                      onChange={(e) => setLine(idx, { working_hrs: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tax type</label>
                    <select value={L.tax_type} onChange={(e) => setLine(idx, { tax_type: e.target.value })}>
                      <option value="">None</option>
                      <option value="CGST-SGST">CGST-SGST</option>
                      <option value="IGST">IGST</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tax %</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={L.tax_percentage}
                      onChange={(e) => setLine(idx, { tax_percentage: e.target.value })}
                    />
                  </div>
                </div>
                <label style={{ fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={L.main_equipment}
                    onChange={(e) => setLine(idx, { main_equipment: e.target.checked })}
                  />{" "}
                  Main equipment
                </label>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="crane-btn" onClick={() => setStep(1)} disabled={busy}>
                ← Back
              </button>
              <button type="button" className="crane-btn crane-btn--primary" onClick={submit} disabled={busy}>
                {busy ? "Creating work order…" : "Create work order"}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModulePage>
  );
}
